import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../design-system/primitives/Icon';
import { apiService } from '../../services/api';
import { relTime } from './format';

const DEBOUNCE_MS = 250;

/**
 * Combobox over organizations and users. Results come from the backend
 * (search is over name/email/company); an empty query lists the most
 * recently active accounts so the page is useful before typing anything.
 */
const EntitySearch = ({ onSelect }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ organizations: [], users: [] });
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);
  const listId = useRef(`qa-search-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(() => {
      apiService.qaEntities(q)
        .then((res) => { setResults(res.data); setActive(-1); })
        .catch(() => setResults({ organizations: [], users: [] }))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const flat = [
    ...results.organizations.map((o) => ({ type: 'org', id: o.id, title: o.name, sub: `${o.member_count} member${o.member_count === 1 ? '' : 's'} · active ${relTime(o.last_activity_at)}`, icon: 'building-2' })),
    ...results.users.map((u) => ({ type: 'user', id: u.id, title: u.full_name || u.username, sub: `${u.email}${u.organization ? ` · ${u.organization.name}` : ''} · active ${relTime(u.last_activity_at)}`, icon: 'user' })),
  ];

  const choose = (item) => {
    onSelect(item);
    setOpen(false);
    setQ('');
  };

  const onKeyDown = (e) => {
    if (!open) { if (e.key === 'ArrowDown') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && active >= 0 && flat[active]) { e.preventDefault(); choose(flat[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  let idx = -1;
  const group = (label, items) => items.length > 0 && (
    <div key={label}>
      <div className="qa-search__group">{label}</div>
      {items.map((item) => {
        idx += 1;
        const i = idx;
        return (
          <button
            key={`${item.type}-${item.id}`}
            type="button"
            className={`qa-search__item ${i === active ? 'qa-search__item--active' : ''}`}
            onMouseEnter={() => setActive(i)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(item)}
          >
            <Icon name={item.icon} size={13} />
            <span className="qa-search__item-text">
              <span className="qa-search__item-title">{item.title}</span>
              <span className="qa-search__item-sub">{item.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="qa-search" ref={rootRef}>
      <div className="qa-search__field">
        <Icon name="search" size={14} />
        <input
          value={q}
          placeholder="Search organizations or users"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
        />
        {loading && <Icon name="loader-2" size={13} className="qa-spin" />}
      </div>
      {open && (
        <div className="qa-search__menu" role="listbox" id={listId}>
          {flat.length === 0 && !loading && <div className="qa-search__empty">No matches</div>}
          {group('Organizations', flat.filter((f) => f.type === 'org'))}
          {group('Users', flat.filter((f) => f.type === 'user'))}
        </div>
      )}
    </div>
  );
};

export default EntitySearch;
